import { useState, useRef, useCallback, useEffect } from "react";
import { HiChevronRight } from "react-icons/hi";

/**
 * A wrapper component that provides consistent horizontal scroll UX for tables:
 * - Always-visible scrollbar
 * - Gradient overlay with chevron indicator when more content exists
 * - Optional max height constraint
 *
 * Usage:
 * <ScrollableTableWrapper maxHeight="600px">
 *   <Table>...</Table>
 * </ScrollableTableWrapper>
 */
export const ScrollableTableWrapper = ({
  children,
  height = "600px",
  className = "",
}) => {
  const [showScrollHint, setShowScrollHint] = useState(false);
  const scrollContainerRef = useRef(null);

  // Check if horizontal scroll is needed and not at end
  const checkScrollHint = useCallback(() => {
    const container = scrollContainerRef.current;
    if (container) {
      const hasHorizontalScroll = container.scrollWidth > container.clientWidth;
      const isAtEnd =
        container.scrollLeft + container.clientWidth >=
        container.scrollWidth - 10;
      setShowScrollHint(hasHorizontalScroll && !isAtEnd);
    }
  }, []);

  // Check scroll hint on mount, resize, and when children change
  useEffect(() => {
    checkScrollHint();
    window.addEventListener("resize", checkScrollHint);

    // Use MutationObserver to detect when table content changes
    const container = scrollContainerRef.current;
    let observer;
    if (container) {
      observer = new MutationObserver(checkScrollHint);
      observer.observe(container, { childList: true, subtree: true });
    }

    return () => {
      window.removeEventListener("resize", checkScrollHint);
      if (observer) {
        observer.disconnect();
      }
    };
  }, [checkScrollHint]);

  return (
    <div className={`relative w-full ${className}`}>
      {/* Scroll hint indicator - offset from right to not cover vertical scrollbar */}
      {showScrollHint && (
        <div
          className="pointer-events-none absolute right-3 top-0 z-10 flex w-16 items-center justify-end bg-gradient-to-l from-white via-white/80 to-transparent"
          style={{ height: "calc(100% - 12px)" }}
        >
          <div className="mr-2 flex animate-pulse items-center text-gray-400">
            <HiChevronRight className="h-6 w-6" />
          </div>
        </div>
      )}
      <div
        ref={scrollContainerRef}
        onScroll={checkScrollHint}
        className="scrollbar-always-visible min-h-full w-full"
        style={{ height }}
      >
        {children}
      </div>
    </div>
  );
};
